import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

export default function BotConfig() {
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Instances
  const [instances, setInstances] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDevice, setNewDevice] = useState('');
  const [expandedLogs, setExpandedLogs] = useState(null);
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const logTimer = useRef(null);

  // Diagnostics
  const [diagSummary, setDiagSummary] = useState(null);
  const [diagExpanded, setDiagExpanded] = useState(false);
  const [diagList, setDiagList] = useState([]);
  const [diagLoading, setDiagLoading] = useState(false);

  const loadInstances = useCallback(() => {
    api.getInstances().then(d => setInstances(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadInstances(); const t = setInterval(loadInstances, 10000); return () => clearInterval(t); }, [loadInstances]);

  // Diagnostics polling
  useEffect(() => {
    const fetchDiag = () => api.getDiagnosticsSummary().then(setDiagSummary).catch(() => {});
    fetchDiag();
    const t = setInterval(fetchDiag, 30000);
    return () => clearInterval(t);
  }, []);

  // Log polling
  useEffect(() => {
    if (logTimer.current) clearInterval(logTimer.current);
    if (!expandedLogs) { setLogs([]); return; }
    const fetchLogs = () => api.getInstanceLogs(expandedLogs, 80).then(d => setLogs(d.lines || [])).catch(() => {});
    fetchLogs();
    logTimer.current = setInterval(fetchLogs, 2000);
    return () => { if (logTimer.current) clearInterval(logTimer.current); };
  }, [expandedLogs]);

  const handleCreate = async () => {
    if (!newName.trim() || !newDevice.trim()) return;
    try {
      await api.createInstance({ name: newName.trim(), device: newDevice.trim() });
      setShowCreate(false); setNewName(''); setNewDevice('');
      loadInstances();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleStart = async (id) => {
    try { await api.startInstance(id); loadInstances(); } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleStop = async (id) => {
    try { await api.stopInstance(id); loadInstances(); } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta instancia?')) return;
    try { await api.deleteInstance(id); loadInstances(); } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleSaveInstanceConfig = async (id, newConfig) => {
    try {
      await api.updateInstance(id, { config: newConfig });
      loadInstances();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = () => {
    fetch('/api/bot/config')
      .then(r => r.json())
      .then(d => { setConfig(d.data); setPresets(d.presets || {}); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const save = () => {
    setSaving(true);
    fetch('/api/bot/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    })
      .then(r => r.json())
      .then(d => { setConfig(d.data); setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000); })
      .catch(() => setSaving(false));
  };

  const applyPreset = (mode) => {
    fetch(`/api/bot/config/preset/${mode}`, { method: 'POST' })
      .then(r => r.json())
      .then(d => { setConfig(d.data); setSaved(true); setTimeout(() => setSaved(false), 2000); });
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Carregando...</div>;
  if (!config) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--accent-r)' }}>Erro ao carregar config</div>;

  const statusColor = { running: 'var(--accent-g)', starting: 'var(--accent-a)', stopping: 'var(--accent-a)', error: 'var(--accent-r)', stopped: 'var(--text3)' };
  const statusLabel = { running: 'Rodando', starting: 'Iniciando...', stopping: 'Parando...', error: 'Erro', stopped: 'Parado' };

  const loadDiagList = () => {
    setDiagLoading(true);
    api.getDiagnostics({ reviewed: 'false' })
      .then(d => { setDiagList(d.data || []); setDiagLoading(false); })
      .catch(() => setDiagLoading(false));
  };

  const handleReview = async (date, folder) => {
    try {
      await api.reviewDiagnostic(date, folder);
      setDiagList(prev => prev.filter(d => !(d._date === date && d._folder === folder)));
      setDiagSummary(prev => prev ? { ...prev, unreviewed_count: Math.max(0, prev.unreviewed_count - 1) } : prev);
    } catch {}
  };

  const eventLabels = {
    organic_page_name_missing: 'Page name nao encontrado (organico)',
    ad_unknown_page: 'Ad com Unknown Page',
    capture_fb_link: 'Falha ao capturar FB link',
    capture_page_url: 'Falha ao capturar Page URL',
    capture_real_url: 'Falha ao capturar Real URL',
    reels_recovery_failed: 'Nao conseguiu voltar aos Reels',
    feed_recovery_failed: 'Nao conseguiu voltar ao Feed',
    stuck_in_reels: 'Bot travado nos Reels',
    audio_failed_2x: 'Audio falhou 2x',
    author_not_found: 'Autor nao encontrado na tela',
  };

  return (
    <div style={s.page}>
      {/* ── Diagnostics Alert ── */}
      {diagSummary && diagSummary.unreviewed_count > 0 && (
        <div style={s.diagAlert} onClick={() => { setDiagExpanded(!diagExpanded); if (!diagExpanded) loadDiagList(); }}>
          <div style={s.diagAlertHeader}>
            <span style={s.diagBadge}>{diagSummary.unreviewed_count}</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {diagSummary.unreviewed_count === 1 ? 'Evento para analisar' : 'Eventos para analisar'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
              {Object.entries(diagSummary.by_event || {}).map(([k, v]) => `${eventLabels[k] || k}: ${v}`).join(' | ')}
            </span>
            <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text3)' }}>{diagExpanded ? '▲' : '▼'}</span>
          </div>
          {diagExpanded && (
            <div style={s.diagList} onClick={e => e.stopPropagation()}>
              {diagLoading ? (
                <div style={{ padding: 12, color: 'var(--text3)', fontSize: 11 }}>Carregando...</div>
              ) : diagList.length === 0 ? (
                <div style={{ padding: 12, color: 'var(--text3)', fontSize: 11 }}>Nenhum evento pendente</div>
              ) : diagList.map((d, i) => (
                <div key={i} style={s.diagItem}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{eventLabels[d.event] || d.event}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                      {new Date(d.timestamp).toLocaleString('pt-BR')}
                      {d.screen_context ? ` | ctx: ${d.screen_context}` : ''}
                      {d.page_name ? ` | page: ${d.page_name}` : ''}
                      {d.mode ? ` | ${d.mode}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {d.has_screenshot && (
                      <a href={`/api/diagnostics/${d._date}/${d._folder}/screenshot.png`} target="_blank" rel="noreferrer"
                        style={s.diagLink}>Screenshot</a>
                    )}
                    {d.has_dump && (
                      <a href={`/api/diagnostics/${d._date}/${d._folder}/dump.xml`} target="_blank" rel="noreferrer"
                        style={s.diagLink}>XML</a>
                    )}
                    <button style={s.diagReviewBtn} onClick={() => handleReview(d._date, d._folder)}>OK</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Instâncias ── */}
      <Section title="Instancias do Bot">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {instances.filter(i => i.status === 'running').length} de {instances.length} instancias rodando
          </div>
          <button style={s.btn} onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancelar' : '+ Nova Instancia'}
          </button>
        </div>

        {/* Criar nova */}
        {showCreate && (
          <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16, background: 'var(--surface2)' }}>
            <Row>
              <div style={{ flex: '1 1 200px' }}>
                <label style={s.label}>Nome</label>
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="GeeLark-2" style={s.input} />
              </div>
              <div style={{ flex: '1 1 250px' }}>
                <label style={s.label}>Device (ADB address)</label>
                <input value={newDevice} onChange={e => setNewDevice(e.target.value)} placeholder="162.128.207.240:20041" style={s.input} />
              </div>
            </Row>
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)' }}>
              A config sera copiada do template default. Ajuste depois de criar.
            </div>
            <button style={{ ...s.btn, marginTop: 12 }} onClick={handleCreate}>Criar Instancia</button>
          </div>
        )}

        {/* Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {instances.map(inst => (
            <div key={inst.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--surface2)' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[inst.status] || 'var(--text3)', flexShrink: 0, boxShadow: inst.status === 'running' ? `0 0 6px ${statusColor.running}` : 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{inst.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{inst.device} · {statusLabel[inst.status] || inst.status} · {inst.config?.mining_mode || 'feed'}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {inst.status === 'running' ? (
                    <button style={{ ...s.presetBtn, color: 'var(--accent-r)', borderColor: 'var(--accent-r)' }} onClick={() => handleStop(inst.id)}>Parar</button>
                  ) : inst.status === 'starting' || inst.status === 'stopping' ? (
                    <button style={{ ...s.presetBtn, opacity: 0.5 }} disabled>{statusLabel[inst.status]}</button>
                  ) : (
                    <button style={{ ...s.presetBtn, color: 'var(--accent-g)', borderColor: 'var(--accent-g)' }} onClick={() => handleStart(inst.id)}>Iniciar</button>
                  )}
                  <button style={s.presetBtn} onClick={() => setExpandedLogs(expandedLogs === inst.id ? null : inst.id)}>
                    {expandedLogs === inst.id ? 'Fechar Logs' : 'Logs'}
                  </button>
                  <button style={s.presetBtn} onClick={() => setExpandedConfig(expandedConfig === inst.id ? null : inst.id)}>
                    {expandedConfig === inst.id ? 'Fechar Config' : 'Config'}
                  </button>
                  {inst.status !== 'running' && (
                    <button style={{ ...s.presetBtn, color: 'var(--accent-r)', fontSize: 10 }} onClick={() => handleDelete(inst.id)}>Excluir</button>
                  )}
                </div>
              </div>

              {/* Logs expandidos */}
              {expandedLogs === inst.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: 12, background: 'var(--bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>Ultimas {logs.length} linhas (auto-refresh 2s)</span>
                  </div>
                  <pre style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'var(--mono)', maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5, margin: 0 }}>
                    {logs.length > 0 ? logs.join('') : 'Sem logs disponíveis'}
                  </pre>
                </div>
              )}

              {/* Config expandido */}
              {expandedConfig === inst.id && (
                <InstanceConfig
                  config={inst.config || {}}
                  onSave={(newCfg) => handleSaveInstanceConfig(inst.id, newCfg)}
                />
              )}
            </div>
          ))}
          {instances.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
              Nenhuma instancia criada. Clique em "+ Nova Instancia" para começar.
            </div>
          )}
        </div>
      </Section>

      {/* ── Config Global (Template Default) ── */}
      <div style={s.topBar}>
        <div>
          <div style={s.title}>Template Default</div>
          <div style={s.subtitle}>Config padrao copiada para novas instancias</div>
          <div style={s.subtitle}>Parametros de mineracao, sessoes e comportamento</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--accent-g)', fontSize: 12, fontWeight: 600 }}>Salvo!</span>}
          <button style={{ ...s.btn, background: config.bot_enabled ? 'var(--accent-g)' : 'var(--accent-r)' }}
            onClick={() => { set('bot_enabled', !config.bot_enabled); }}>
            {config.bot_enabled ? 'Bot Ativo' : 'Bot Pausado'}
          </button>
          <button style={s.btn} onClick={save} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Presets */}
      <Section title="Perfil de Operacao">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['conservative', 'moderate', 'aggressive'].map(mode => (
            <button key={mode} onClick={() => applyPreset(mode)}
              style={{ ...s.presetBtn, ...(config.profile_mode === mode ? s.presetActive : {}) }}>
              {mode === 'conservative' ? 'Conservador' : mode === 'moderate' ? 'Moderado' : 'Agressivo'}
            </button>
          ))}
          <button style={{ ...s.presetBtn, ...(config.profile_mode === 'custom' ? s.presetActive : {}) }}
            onClick={() => set('profile_mode', 'custom')}>
            Personalizado
          </button>
        </div>
        <div style={s.hint}>
          {config.profile_mode === 'conservative' && 'Simula usuario casual (~30 min/dia, 5 sessoes). Risco muito baixo.'}
          {config.profile_mode === 'moderate' && 'Simula usuario ativo (~50 min/dia, 7 sessoes). Risco baixo.'}
          {config.profile_mode === 'aggressive' && 'Simula heavy user (~90 min/dia, 9 sessoes). Risco medio.'}
          {config.profile_mode === 'custom' && 'Configuracao manual — ajuste cada parametro individualmente.'}
        </div>
      </Section>

      {/* Modo de Mineracao */}
      <Section title="Modo de Mineracao">
        <div style={{ display: 'flex', gap: 8 }}>
          {[['feed', 'Feed', 'Scroll no feed principal — ads entre posts normais'],
            ['reels', 'Reels', 'Scroll vertical nos Reels — ads entre videos curtos'],
            ['both', 'Ambos', 'Alterna entre Feed e Reels a cada sessao']
          ].map(([mode, label, desc]) => (
            <button key={mode} onClick={() => set('mining_mode', mode)}
              style={{ ...s.presetBtn, flex: 1, textAlign: 'center', ...(config.mining_mode === mode ? s.presetActive : {}) }}>
              <div style={{ fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: 10, marginTop: 2, opacity: 0.8 }}>{desc}</div>
            </button>
          ))}
        </div>
      </Section>

      {/* Nichos */}
      <Section title="Nichos Alvo">
        <NicheMap nicheMap={config.niche_map || {}} onChange={v => set('niche_map', v)} />
      </Section>

      {/* Warm-up */}
      <Section title="Warm-up">
        <Row>
          <Toggle label="Ativar warm-up" value={config.warmup_enabled} onChange={v => set('warmup_enabled', v)} />
          <NumberField label="Duracao (min)" value={config.warmup_duration_min} onChange={v => set('warmup_duration_min', v)} min={1} max={10} />
        </Row>
        {config.warmup_enabled && (
          <div style={{ marginTop: 12 }}>
            <label style={s.label}>Termos de busca do warm-up</label>
            <TagInput value={config.warmup_search_terms || []} onChange={v => set('warmup_search_terms', v)}
              placeholder="Adicionar termo (Enter)" />
          </div>
        )}
      </Section>

      {/* Sessões */}
      <Section title="Sessoes">
        <Row>
          <NumberField label="Sessoes por dia" value={config.sessions_per_day} onChange={v => set('sessions_per_day', v)} min={1} max={15} />
          <NumberField label="Scrolls min/sessao" value={config.session_min_scrolls} onChange={v => set('session_min_scrolls', v)} min={10} max={200} />
          <NumberField label="Scrolls max/sessao" value={config.session_max_scrolls} onChange={v => set('session_max_scrolls', v)} min={10} max={300} />
        </Row>
        <Row>
          <NumberField label="Duracao min (min)" value={config.session_min_duration_min} onChange={v => set('session_min_duration_min', v)} min={2} max={30} />
          <NumberField label="Duracao max (min)" value={config.session_max_duration_min} onChange={v => set('session_max_duration_min', v)} min={5} max={60} />
        </Row>
      </Section>

      {/* Pausas */}
      <Section title="Pausas entre Sessoes">
        <Row>
          <NumberField label="Pausa min (min)" value={config.break_min_minutes} onChange={v => set('break_min_minutes', v)} min={5} max={180} />
          <NumberField label="Pausa max (min)" value={config.break_max_minutes} onChange={v => set('break_max_minutes', v)} min={10} max={300} />
        </Row>
      </Section>

      {/* Horários */}
      <Section title="Horarios (EST)">
        <Row>
          <NumberField label="Inicio atividade (hora)" value={config.active_start_hour} onChange={v => set('active_start_hour', v)} min={0} max={23} suffix="h" />
          <NumberField label="Fim atividade (hora)" value={config.active_end_hour} onChange={v => set('active_end_hour', v)} min={0} max={23} suffix="h" />
          <NumberField label="Gap noturno (horas)" value={config.night_gap_hours} onChange={v => set('night_gap_hours', v)} min={4} max={12} suffix="h" />
        </Row>
        <div style={s.hint}>
          Horario ativo: {config.active_start_hour}:00 - {config.active_end_hour}:00 EST | Gap noturno: {config.night_gap_hours}h sem atividade
        </div>
      </Section>

      {/* Limites */}
      <Section title="Limites Diarios">
        <Row>
          <NumberField label="Max scrolls/dia" value={config.max_scrolls_per_day} onChange={v => set('max_scrolls_per_day', v)} min={50} max={2000} />
          <NumberField label="Max likes/dia" value={config.max_likes_per_day} onChange={v => set('max_likes_per_day', v)} min={0} max={300} />
          <NumberField label="Reducao fim de semana (%)" value={config.weekend_reduction_pct} onChange={v => set('weekend_reduction_pct', v)} min={0} max={80} suffix="%" />
        </Row>
      </Section>

      {/* Comportamento */}
      <Section title="Comportamento Humano">
        <Row>
          <NumberField label="Chance scroll back (%)" value={config.scroll_back_chance_pct} onChange={v => set('scroll_back_chance_pct', v)} min={0} max={20} suffix="%" />
          <NumberField label="Chance micro-interacao (%)" value={config.micro_interact_chance_pct} onChange={v => set('micro_interact_chance_pct', v)} min={0} max={25} suffix="%" />
          <NumberField label="Pausa a cada N scrolls" value={config.session_break_every_scrolls} onChange={v => set('session_break_every_scrolls', v)} min={10} max={100} />
        </Row>
      </Section>

      {/* Audio */}
      <Section title="Audio e Classificacao">
        <Row>
          <NumberField label="Gravacao audio (seg)" value={config.audio_record_seconds} onChange={v => set('audio_record_seconds', v)} min={10} max={60} suffix="s" />
        </Row>
      </Section>

      {/* Filtros de Formato */}
      <Section title="Filtros de Formato de Anuncio">
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
          Filtros de formato — Partnership e Verificadas ignorados por padrao.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Toggle label="Ignorar Multi-parte (Part X/Y)"
            value={(config.ad_format_filters || {}).skip_multi_part || false}
            onChange={v => set('ad_format_filters', { ...config.ad_format_filters, skip_multi_part: v })} />
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: -6, marginLeft: 4 }}>
            Ignora completamente ads multi-parte (Part 1 incluso). Quando desativado, Part 1 processa e Part 2+ sempre ignorado
          </div>
          <Toggle label="Ignorar Carrossel de Produtos"
            value={(config.ad_format_filters || {}).skip_carousel_products || false}
            onChange={v => set('ad_format_filters', { ...config.ad_format_filters, skip_carousel_products: v })} />
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: -6, marginLeft: 4 }}>
            Anuncios com catalogo de produtos e "Tap image to shop/learn" — formato e-commerce
          </div>
          <Toggle label="Ignorar Partnership Ads"
            value={(config.ad_format_filters || {}).skip_partnership ?? true}
            onChange={v => set('ad_format_filters', { ...config.ad_format_filters, skip_partnership: v })} />
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: -6, marginLeft: 4 }}>
            Anuncios com criadores parceiros (ex: "Fiverr and ConnerArdman") — ignora por padrao
          </div>
          <Toggle label="Ignorar Paginas Verificadas"
            value={(config.ad_format_filters || {}).skip_verified_pages ?? true}
            onChange={v => set('ad_format_filters', { ...config.ad_format_filters, skip_verified_pages: v })} />
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: -6, marginLeft: 4 }}>
            Pula ads de paginas com badge verificado (azul) — grandes marcas como BetterHelp, TikTok, etc
          </div>
        </div>
      </Section>

      {/* Projeção */}
      <Section title="Projecao Diaria (estimativa)">
        <Projection config={config} />
      </Section>
    </div>
  );
}


function InstanceConfig({ config: initialConfig, onSave }) {
  const [cfg, setCfg] = useState({ ...initialConfig });
  const [instSaving, setInstSaving] = useState(false);
  const [instSaved, setInstSaved] = useState(false);
  const setC = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setInstSaving(true);
    await onSave(cfg);
    setInstSaving(false);
    setInstSaved(true);
    setTimeout(() => setInstSaved(false), 2000);
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Configuracao da Instancia</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {instSaved && <span style={{ color: 'var(--accent-g)', fontSize: 11 }}>Salvo!</span>}
          <button style={{ ...s.btn, fontSize: 11, padding: '5px 12px' }} onClick={handleSave} disabled={instSaving}>
            {instSaving ? 'Salvando...' : 'Salvar Config'}
          </button>
        </div>
      </div>

      <Row>
        <Toggle label="Bot habilitado" value={cfg.bot_enabled !== false} onChange={v => setC('bot_enabled', v)} />
        <div style={{ flex: '1 1 140px' }}>
          <label style={s.label}>Modo mineracao</label>
          <select value={cfg.mining_mode || 'feed'} onChange={e => setC('mining_mode', e.target.value)}
            style={{ ...s.input, cursor: 'pointer' }}>
            <option value="feed">Feed</option>
            <option value="reels">Reels</option>
            <option value="both">Ambos</option>
          </select>
        </div>
      </Row>
      <div style={{ height: 8 }} />
      <Row>
        <NumberField label="Sessoes/dia" value={cfg.sessions_per_day} onChange={v => setC('sessions_per_day', v)} min={1} max={15} />
        <NumberField label="Scrolls min" value={cfg.session_min_scrolls} onChange={v => setC('session_min_scrolls', v)} min={10} max={200} />
        <NumberField label="Scrolls max" value={cfg.session_max_scrolls} onChange={v => setC('session_max_scrolls', v)} min={10} max={300} />
        <NumberField label="Max scrolls/dia" value={cfg.max_scrolls_per_day} onChange={v => setC('max_scrolls_per_day', v)} min={50} max={2000} />
      </Row>
      <div style={{ height: 8 }} />
      <Row>
        <NumberField label="Pausa min (min)" value={cfg.break_min_minutes} onChange={v => setC('break_min_minutes', v)} min={5} max={180} />
        <NumberField label="Pausa max (min)" value={cfg.break_max_minutes} onChange={v => setC('break_max_minutes', v)} min={10} max={300} />
        <NumberField label="Audio (seg)" value={cfg.audio_record_seconds} onChange={v => setC('audio_record_seconds', v)} min={10} max={60} suffix="s" />
      </Row>
      <div style={{ height: 8 }} />
      <Row>
        <NumberField label="Inicio (hora EST)" value={cfg.active_start_hour} onChange={v => setC('active_start_hour', v)} min={0} max={23} suffix="h" />
        <NumberField label="Fim (hora EST)" value={cfg.active_end_hour} onChange={v => setC('active_end_hour', v)} min={0} max={23} suffix="h" />
        <NumberField label="Reducao fds (%)" value={cfg.weekend_reduction_pct} onChange={v => setC('weekend_reduction_pct', v)} min={0} max={80} suffix="%" />
      </Row>
      <div style={{ height: 8 }} />
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Filtros de Formato</div>
      <Row>
        <Toggle label="Ignorar Multi-parte" value={(cfg.ad_format_filters || {}).skip_multi_part || false}
          onChange={v => setC('ad_format_filters', { ...cfg.ad_format_filters, skip_multi_part: v })} />
        <Toggle label="Ignorar Carrossel" value={(cfg.ad_format_filters || {}).skip_carousel_products || false}
          onChange={v => setC('ad_format_filters', { ...cfg.ad_format_filters, skip_carousel_products: v })} />
        <Toggle label="Ignorar Partnership" value={(cfg.ad_format_filters || {}).skip_partnership ?? true}
          onChange={v => setC('ad_format_filters', { ...cfg.ad_format_filters, skip_partnership: v })} />
        <Toggle label="Ignorar Verificadas" value={(cfg.ad_format_filters || {}).skip_verified_pages ?? true}
          onChange={v => setC('ad_format_filters', { ...cfg.ad_format_filters, skip_verified_pages: v })} />
      </Row>
    </div>
  );
}


function Projection({ config }) {
  const avgScrolls = (config.session_min_scrolls + config.session_max_scrolls) / 2;
  const totalScrolls = Math.min(avgScrolls * config.sessions_per_day, config.max_scrolls_per_day);
  const adsFound = Math.round(totalScrolls * 0.075);
  const adsRelevant = Math.round(adsFound * 0.5);
  const avgDuration = (config.session_min_duration_min + config.session_max_duration_min) / 2;
  const totalMin = Math.round(avgDuration * config.sessions_per_day);

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <StatCard label="Tempo ativo/dia" value={`${totalMin} min`} />
      <StatCard label="Scrolls/dia" value={totalScrolls.toFixed(0)} />
      <StatCard label="Ads encontrados" value={`~${adsFound}`} />
      <StatCard label="Ads relevantes" value={`~${adsRelevant}`} />
      <StatCard label="Ads/mes" value={`~${(adsRelevant * 30).toFixed(0)}`} accent />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 16px', minWidth: 120 }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent ? 'var(--accent)' : 'var(--text)' }}>{value}</div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>{children}</div>;
}

function NumberField({ label, value, onChange, min, max, suffix }) {
  return (
    <div style={{ flex: '1 1 140px' }}>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input type="number" value={value} min={min} max={max}
          onChange={e => onChange(parseInt(e.target.value) || 0)}
          style={s.input} />
        {suffix && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{suffix}</span>}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div style={{ flex: '1 1 140px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <label style={s.label}>{label}</label>
      <button onClick={() => onChange(!value)}
        style={{ ...s.toggleBtn, background: value ? 'var(--accent-g)' : 'var(--surface3)' }}>
        <div style={{ ...s.toggleKnob, transform: value ? 'translateX(16px)' : 'translateX(0)' }} />
      </button>
    </div>
  );
}

function TagInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {value.map((tag, i) => (
          <span key={i} style={s.tag}>
            {tag}
            <button onClick={() => remove(i)} style={s.tagRemove}>x</button>
          </span>
        ))}
      </div>
      <input value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={placeholder} style={{ ...s.input, width: 250 }} />
    </div>
  );
}


function NicheMap({ nicheMap, onChange }) {
  const [expanded, setExpanded] = useState(null);
  const [newKw, setNewKw] = useState('');

  const toggleNiche = (key) => {
    const updated = { ...nicheMap, [key]: { ...nicheMap[key], enabled: !nicheMap[key].enabled } };
    onChange(updated);
  };

  const removeKeyword = (nicheKey, idx) => {
    const updated = { ...nicheMap, [nicheKey]: { ...nicheMap[nicheKey], keywords: nicheMap[nicheKey].keywords.filter((_, i) => i !== idx) } };
    onChange(updated);
  };

  const addKeyword = (nicheKey) => {
    const trimmed = newKw.trim().toLowerCase();
    if (!trimmed || nicheMap[nicheKey].keywords.includes(trimmed)) { setNewKw(''); return; }
    const updated = { ...nicheMap, [nicheKey]: { ...nicheMap[nicheKey], keywords: [...nicheMap[nicheKey].keywords, trimmed] } };
    onChange(updated);
    setNewKw('');
  };

  const enabledCount = Object.values(nicheMap).filter(n => n.enabled).length;
  const totalCount = Object.keys(nicheMap).length;

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
        {enabledCount} de {totalCount} nichos ativos — clique para expandir keywords
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(nicheMap).map(([key, niche]) => (
          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
            background: niche.enabled ? 'var(--surface)' : 'var(--surface2)', opacity: niche.enabled ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 10, cursor: 'pointer' }}
              onClick={() => setExpanded(expanded === key ? null : key)}>
              <button onClick={(e) => { e.stopPropagation(); toggleNiche(key); }}
                style={{ ...s.toggleBtn, width: 32, height: 18, background: niche.enabled ? 'var(--accent-g)' : 'var(--surface3)' }}>
                <div style={{ ...s.toggleKnob, width: 14, height: 14, transform: niche.enabled ? 'translateX(14px)' : 'translateX(0)' }} />
              </button>
              <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{niche.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{niche.keywords?.length || 0} keywords</span>
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>{expanded === key ? '▲' : '▼'}</span>
            </div>
            {expanded === key && (
              <div style={{ padding: '4px 12px 12px', borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {(niche.keywords || []).map((kw, i) => (
                    <span key={i} style={s.tag}>
                      {kw}
                      <button onClick={() => removeKeyword(key, i)} style={s.tagRemove}>x</button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={newKw} onChange={e => setNewKw(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(key); } }}
                    placeholder="Adicionar keyword (Enter)" style={{ ...s.input, width: 220, fontSize: 11 }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const s = {
  page: { padding: '24px', maxWidth: 1000, margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },
  btn: {
    padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    padding: 20, marginBottom: 16,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, marginBottom: 14, color: 'var(--text)' },
  label: { fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4, fontWeight: 500 },
  input: {
    padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--surface2)', color: 'var(--text)', fontSize: 13,
    fontFamily: 'inherit', width: '100%', outline: 'none',
  },
  hint: { fontSize: 11, color: 'var(--text3)', marginTop: 8 },
  presetBtn: {
    padding: '6px 14px', border: '1px solid var(--border)', background: 'var(--surface2)',
    color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  presetActive: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  toggleBtn: {
    width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
    position: 'relative', transition: 'background 0.2s',
  },
  toggleKnob: {
    width: 16, height: 16, borderRadius: '50%', background: 'var(--text)',
    position: 'absolute', top: 2, left: 2, transition: 'transform 0.2s',
  },
  tag: {
    padding: '3px 10px', background: 'rgba(139,92,246,0.08)', color: 'var(--accent-p)',
    borderRadius: 4, fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
  },
  tagRemove: {
    background: 'none', border: 'none', color: 'var(--accent-p)', cursor: 'pointer',
    fontSize: 11, padding: 0, fontWeight: 700,
  },

  // Diagnostics
  diagAlert: {
    background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
    borderRadius: 8, padding: '12px 16px', marginBottom: 16, cursor: 'pointer',
  },
  diagAlertHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  diagBadge: {
    background: 'var(--accent-a)', color: '#000', fontWeight: 700, fontSize: 11,
    padding: '2px 8px', borderRadius: 10, minWidth: 20, textAlign: 'center',
  },
  diagList: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 },
  diagItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', background: 'var(--surface2)', borderRadius: 6,
    border: '1px solid var(--border)',
  },
  diagLink: {
    fontSize: 10, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600,
    padding: '3px 8px', background: 'rgba(96,165,250,0.1)', borderRadius: 4,
  },
  diagReviewBtn: {
    fontSize: 10, fontWeight: 600, fontFamily: 'inherit', padding: '3px 10px',
    background: 'var(--surface3)', color: 'var(--text)', border: '1px solid var(--border)',
    borderRadius: 4, cursor: 'pointer',
  },
};
